/* The Scores workspace's view model. Pure types — no React, no fetch, no formatting.
 *
 * These exist so a component never holds a raw `ScoreSummary` plus four loose facts it has to
 * re-derive. Every screen-level question this workspace answers — *is it healthy, is it below its
 * target, did it move, is the evaluation stale, who owns it* — is a field here, computed once in
 * `rows.ts` and read everywhere else.
 *
 * **Nothing in this file invents a fact.** Each field names where it comes from:
 *
 * | field        | source                                                              |
 * |--------------|---------------------------------------------------------------------|
 * | `summary`    | `GET /v1/scoring/scores/latest`                                     |
 * | `unscored`   | `GET /v1/catalog/datasets/facts` (profile baseline + last profiled) |
 * | `target`     | `GET /v1/contracts` — an **active** contract's `min_quality_score`  |
 * | `owner`      | that contract's `producer_subject`                                  |
 * | `movement`   | `GET /v1/scoring/datasets/{id}/trend`, one request per dataset      |
 *
 * The one field with no source is the one that is therefore absent: there is no `environment`,
 * because `catalog.dataset` does not carry one and a column that guessed would be worse than no
 * column. The schema name plays that role instead, and it is real.
 */

import type { ScoreSummary } from '../api/scoring';

import type { DeltaDisplay, UnscoredReading } from './presentation';

/** Where a dataset sits, once its score, its target and its coverage are all taken together.
 *
 * `unscored` is a fourth member rather than the bottom of the range, and that is the whole reason
 * this is an enum and not a number. A dataset nobody has measured is a *coverage* gap; sorting it
 * beside a failing one implies somebody looked and found nothing good, which is the opposite of
 * what happened.
 */
export type ScoreStatus = 'healthy' | 'warning' | 'critical' | 'unscored';

/** A quality target, read from a contract rather than configured on this screen.
 *
 * `core.contracts` already holds `min_quality_score` as a signed, versioned term between a producer
 * and a consumer. A second threshold store here would be a second definition of "good enough" that
 * drifts from the one two parties actually signed — so this is a projection of that contract, never
 * an independent setting, and `contractName` is on it so a breach names the promise it broke.
 */
export interface QualityTarget {
  readonly minScore: number;
  readonly contractId: string;
  readonly contractName: string;
  /** The contract's own `breach_severity`. A regulator-facing contract escalates differently. */
  readonly severity: 'info' | 'warning' | 'critical';
  /** `producer_subject` — the party who signed up to produce this data to this standard. */
  readonly owner: string;
  readonly consumer: string;
}

/** One dataset's movement, and the four ways there may not be one.
 *
 * A separate object from the row because it is fetched separately and arrives later: the estate
 * listing is one request, and movement is one request *per dataset*. A row therefore renders
 * complete without it, and gains it when it arrives.
 */
export interface Movement {
  readonly delta: DeltaDisplay;
  /** Oldest-first values for the row's sparkline. Nulls preserved — a gap is a finding. */
  readonly points: readonly (number | null)[];
  /** Coverage at the newest point, so "improved because it checked less" is detectable. */
  readonly coverageNow: number | null;
  /** Coverage at the oldest measured point in the window. */
  readonly coverageThen: number | null;
  /** True when the window spans a score-model change, so the two ends are not comparable. */
  readonly crossesModelChange: boolean;
}

/** A dataset as the ledger renders it. Built once by `buildRows`. */
export interface DatasetRow {
  readonly id: string;
  /** `schema.object`, from the catalogue walk. */
  readonly name: string;
  readonly schema: string;
  readonly object: string;
  /** Absent when the dataset has no score at all — see `unscored`. */
  readonly summary: ScoreSummary | undefined;
  /** `null` when the dataset *has* a score. Otherwise which of the three absences applies. */
  readonly unscored: UnscoredReading | null;
  readonly value: number | null;
  readonly coverageRatio: number | null;
  readonly target: QualityTarget | null;
  /** `value - target.minScore`. Negative is a breach. `null` when either side is missing. */
  readonly slack: number | null;
  readonly status: ScoreStatus;
  /** ISO instant of the last score, or `null` for a dataset never scored. */
  readonly evaluatedAt: string | null;
  /** Hours since `evaluatedAt`, or `null`. Held on the row so sorting does not re-parse a date. */
  readonly evaluatedHoursAgo: number | null;
  /** True when a scored dataset has not been re-evaluated inside the staleness window. */
  readonly stale: boolean;
  /** False when the execution behind the score was PARTIAL. */
  readonly evidenceComplete: boolean;
  /** `undefined` until the movement read for this dataset resolves. */
  readonly movement: Movement | undefined;
}

/** One entry in the regression feed. Derived, never stored. */
export interface Regression {
  readonly datasetId: string;
  readonly datasetName: string;
  readonly severity: 'critical' | 'warning' | 'info';
  /** Which detector fired. Stable enough to filter or mute on later. */
  readonly kind:
    | 'score-drop'
    | 'below-target'
    | 'coverage-fell'
    | 'partial-evidence'
    | 'model-change'
    | 'stale-evaluation';
  /** i18n key for the headline. */
  readonly messageKey: string;
  readonly messageArgs: Readonly<Record<string, string | number>>;
  /** i18n key for the supporting line — the threshold, or the contract that was breached. */
  readonly detailKey: string;
  readonly detailArgs: Readonly<Record<string, string | number>>;
  /** Which drawer tab answers this finding. */
  readonly tab: DrawerTab;
}

/** The four questions the drawer answers, in the order a diagnosis runs. */
export type DrawerTab = 'decomposition' | 'change' | 'runs' | 'checks';

/** A column a reader may hide. The first five are never hideable — they are the row's identity,
 * its number, its movement and its verdict, and a table without them is not this table. */
export type OptionalColumn = 'coverage' | 'dimensions' | 'target' | 'evaluated' | 'owner';

export const OPTIONAL_COLUMNS: readonly OptionalColumn[] = [
  'coverage',
  'dimensions',
  'target',
  'evaluated',
  'owner',
];
